import { useEffect } from "react";
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useRouteMatch,
} from "react-router-dom";
import { Box, CircularProgress, Snackbar } from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { Alert } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import TagManager from "react-gtm-module";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";

// Components
import Dialogs from "components/dialogs";
import { Header } from "components/header";
import { Footer } from "components/footer";
// Helpers
import { AppContext, AppProvider, ToastTypes } from "hooks/useApp";
import { AuthProvider } from "hooks/useAuth";
import { CatalogProvider } from "hooks/useCatalog";
import { CatalogDetailProvider } from "hooks/useCatalogDetail";
import { DialogProvider } from "hooks/useDialog";
import { PreferenceProvider } from "hooks/usePreference";
import {
  AuthenticatedRoute,
  PermittedRoute,
  UnauthenticatedRoute,
} from "hooks/useRouter";
import { theme } from "theme";
import { UserRole } from "types/user";
// Pages
import ForgotPassword from "pages/auth/ForgotPassword";
import Login from "pages/auth/Login";
import PasswordRecovery from "pages/auth/PasswordRecovery";
import Register from "pages/auth/Register";
import ResetPassword from "pages/auth/ResetPassword";
import ResetPasswordSuccess from "pages/auth/ResetPasswordSuccess";
import RegisterSuccess from "pages/auth/RegisterSuccess";
import { Contact } from "pages/Contact";
import { CustomPanelOfferings } from "pages/CustomPanelOfferings";
import Catalog from "pages/Catalog";
import { Dashboard } from "pages/Dashboard";
import PanelDetail from "pages/evaluate-panel-options/PanelDetail";
import EvaluatePanelOptions from "pages/evaluate-panel-options";
import { TechnologyPlatform } from "pages/TechnologyPlatform";
import UploadDesigns from "pages/upload-designs";
import UploadTargets from "pages/upload-targets";
import { Homepage } from "pages/Homepage";
import NotFound from "pages/NotFound";

const useStyles = makeStyles({
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
});

const CatalogRoutes = (props: any) => {
  const { path } = useRouteMatch();

  return (
    <CatalogProvider>
      <CatalogDetailProvider>
        <Switch>
          <Route exact path={path}>
            <Catalog {...props} />
          </Route>
          <Route exact path={`${path}/detail/:id`}>
            <PanelDetail key="panel-detail" {...props} />
          </Route>
        </Switch>
      </CatalogDetailProvider>
    </CatalogProvider>
  );
};

const App = () => {
  const classes = useStyles();

  useEffect(() => {
    console.debug(process.env.NODE_ENV);
    // GTM
    if (process.env.REACT_APP_GTM_ID) {
      TagManager.initialize({
        gtmId: process.env.REACT_APP_GTM_ID,
      });
    }

    if (process.env.REACT_APP_COOKIELAW_DOMAIN_SCRIPT) {
      const script = document.createElement("script");
      script.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
      script.type = "text/javascript";
      script.setAttribute("data-document-language", "true");
      script.setAttribute(
        "data-domain-script",
        process.env.REACT_APP_COOKIELAW_DOMAIN_SCRIPT
      );
      script.type = "text/javascript";
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter5Adapter}>
            <AppProvider>
              <AppContext.Consumer>
                {({ loading, toastMessage, setToastMessage }) => {
                  if (loading) {
                    return (
                      <Box
                        width={1}
                        height="100vh"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CircularProgress />
                      </Box>
                    );
                  } else {
                    return (
                      <>
                        <AuthProvider>
                          <DialogProvider>
                            <PreferenceProvider>
                              <Switch>
                                <Route path="*">
                                  <div className={classes.container}>
                                    <Header />
                                    <Box
                                      component="main"
                                      display="flex"
                                      flex={1}
                                    >
                                      <Switch>
                                        <Route
                                          exact
                                          path="/"
                                          component={Homepage}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/login"
                                          component={Login}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/forgot-password"
                                          component={ForgotPassword}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/reset-password"
                                          component={ResetPassword}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/reset-password-success"
                                          component={ResetPasswordSuccess}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/password-recovery"
                                          component={PasswordRecovery}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/register"
                                          component={Register}
                                        />
                                        <UnauthenticatedRoute
                                          exact
                                          path="/register-success"
                                          component={RegisterSuccess}
                                        />
                                        <Route
                                          path="/catalog"
                                          component={CatalogRoutes}
                                        />
                                        <AuthenticatedRoute
                                          exact
                                          path="/upload-targets"
                                          component={UploadTargets}
                                        />
                                        <AuthenticatedRoute
                                          path="/evaluate-panel-options"
                                          component={EvaluatePanelOptions}
                                        />
                                        <PermittedRoute
                                          exact
                                          permissions={[UserRole.Admin]}
                                          path={"/upload-designs"}
                                          component={UploadDesigns}
                                        />
                                        <AuthenticatedRoute
                                          path="/dashboard"
                                          component={Dashboard}
                                        />
                                        <Route
                                          path="/custom-panels"
                                          component={CustomPanelOfferings}
                                        />
                                        <Route
                                          path="/contact"
                                          component={Contact}
                                        />
                                        <Route
                                          path="/technology-platform"
                                          component={TechnologyPlatform}
                                        />
                                        <Route
                                          path="/not-found"
                                          component={NotFound}
                                        />
                                        <Redirect to="/not-found" />
                                      </Switch>
                                    </Box>
                                    <Footer />
                                  </div>
                                </Route>
                              </Switch>

                              <Dialogs />
                            </PreferenceProvider>
                          </DialogProvider>
                        </AuthProvider>
                        {toastMessage && (
                          <Snackbar
                            open={Boolean(toastMessage)}
                            onClose={() => setToastMessage(null)}
                            autoHideDuration={
                              "duration" in toastMessage
                                ? toastMessage.duration
                                : 3000
                            }
                          >
                            <Alert
                              icon={false}
                              variant="filled"
                              severity={toastMessage?.type || ToastTypes.ERROR}
                            >
                              {toastMessage?.message}
                            </Alert>
                          </Snackbar>
                        )}
                      </>
                    );
                  }
                }}
              </AppContext.Consumer>
            </AppProvider>
          </QueryParamProvider>
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
